<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useToast } from 'vue-toast-notification'
import Bs2Title from '@/components/common/layout/Bs2Title.vue'
import GridVue from '@/components/data-visualization/grids/GridVue.vue'
import ButtonDaisy from '@/components/common/buttons/ButtonDaisy.vue'
import { ArrowPathIcon } from '@heroicons/vue/24/outline'
import CegidImportsGridActions from '@/components/features/admin/CegidImportsGridActions.vue'
import CegidRunErrorsAction from '@/components/features/admin/CegidRunErrorsAction.vue'
import {
  useCegidImportsAdminStore,
  type CegidInvoiceError,
  type CegidJobProgress,
  type CegidJobRun,
} from '@/stores/useCegidImportsAdminStore'
import { useWebSocketStore } from '@/stores/useWebSocketStore'

const store = useCegidImportsAdminStore()
const $toast = useToast()

const retrying = ref<Set<string>>(new Set())
function isRetrying(uuid: string): boolean {
  return retrying.value.has(uuid)
}

function fmtDate(value?: string | null): string {
  if (!value) return '—'
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('fr-FR')
}
function fmtDateOnly(value?: string | null): string {
  if (!value) return '—'
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('fr-FR')
}
function statusColor(value: string): string {
  if (value === 'success' || value === 'ok') return '#27ae60'
  if (value === 'error' || value === 'max_retries') return '#c0392b'
  return '#e67e22'
}

const runsColumns = computed(() => [
  { field: 'jobName', headerName: 'Job', minWidth: 150 },
  {
    field: 'status',
    headerName: 'Statut',
    minWidth: 110,
    cellStyle: (p: { value: string }) => ({ color: statusColor(p.value), fontWeight: 600 }),
  },
  { field: 'trigger', headerName: 'Déclenchement', minWidth: 120 },
  { field: 'startedAt', headerName: 'Début', minWidth: 160, valueFormatter: (p: { value: string }) => fmtDate(p.value) },
  {
    field: 'durationMs',
    headerName: 'Durée',
    minWidth: 90,
    valueFormatter: (p: { value: number | null }) => (p.value != null ? `${Math.round(p.value / 1000)}s` : '—'),
  },
  { field: 'counts.processed', headerName: 'Traitées', minWidth: 90 },
  { field: 'counts.ok', headerName: 'OK', minWidth: 70 },
  { field: 'counts.ko', headerName: 'KO', minWidth: 70 },
  { field: 'counts.skipped', headerName: 'Ignorées', minWidth: 90 },
  {
    field: 'actions',
    headerName: 'Détails',
    cellRenderer: CegidRunErrorsAction,
    cellRendererParams: { onView: openRunDetail },
    sortable: false,
    filter: false,
    minWidth: 90,
    maxWidth: 110,
    cellClass: 'border-0 grid-action',
  },
])

// ── Modal de détail des erreurs d'un run ─────────────────────────────────────
const selectedRun = ref<CegidJobRun | null>(null)
function openRunDetail(run: CegidJobRun) {
  selectedRun.value = run
}
function closeRunDetail() {
  selectedRun.value = null
}
const selectedRunErrorBreakdown = computed(() => {
  const errors = selectedRun.value?.errors ?? []
  const byCode = new Map<string, number>()
  for (const e of errors) byCode.set(e.code, (byCode.get(e.code) ?? 0) + 1)
  return [...byCode.entries()].map(([code, count]) => ({ code, count })).sort((a, b) => b.count - a.count)
})

const errorsColumns = computed(() => [
  { field: 'name', headerName: 'Facture', minWidth: 150 },
  { field: 'companyName', headerName: 'Société', minWidth: 180 },
  {
    field: 'invoiceDate',
    headerName: "Date d'émission",
    minWidth: 130,
    valueFormatter: (p: { value: string | null }) => fmtDateOnly(p.value),
  },
  { field: 'paymentState', headerName: 'Paiement', minWidth: 110 },
  { field: 'cegidStatus', headerName: 'Lettrage', minWidth: 100 },
  {
    field: 'status',
    headerName: 'État',
    minWidth: 110,
    cellStyle: (p: { value: string }) => ({ color: statusColor(p.value), fontWeight: 600 }),
  },
  { field: 'errorCode', headerName: 'Code erreur', minWidth: 150 },
  { field: 'errorMessage', headerName: 'Erreur', minWidth: 250, flex: 2 },
  {
    field: 'errorAt',
    headerName: 'Date erreur',
    minWidth: 160,
    valueFormatter: (p: { value: string | null }) => fmtDate(p.value),
  },
  { field: 'retryCount', headerName: 'Retry', minWidth: 80 },
  {
    field: 'actions',
    headerName: '',
    cellRenderer: CegidImportsGridActions,
    cellRendererParams: { onRetry, isRetrying },
    sortable: false,
    filter: false,
    minWidth: 70,
    maxWidth: 80,
    cellClass: 'border-0 grid-action',
  },
])

async function onRetry(row: CegidInvoiceError) {
  if (isRetrying(row.invoiceUuid)) return
  retrying.value = new Set(retrying.value).add(row.invoiceUuid)
  try {
    const res = await store.retryInvoice(row.invoiceUuid)
    if (!res) {
      $toast.error('Erreur lors de la relance')
    } else if (res.result === 'resolved_already_paid') {
      const labels: Record<string, string> = { paid: 'payée', reversed: 'extournée', in_payment: 'en paiement' }
      const state = res.odooPaymentState ?? ''
      $toast.success(`Facture réglée côté Odoo (${labels[state] ?? (state || 'réglée')}) — statut mis à jour, erreur supprimée`)
    } else if (res.result === 'payment_registered') {
      $toast.success('Paiement ré-enregistré dans Odoo — erreur supprimée')
    } else if (res.result === 'payment_failed') {
      $toast.error(`Échec paiement Odoo : ${res.error ?? 'erreur inconnue'}`)
    } else if (res.result === 'still_unresolved') {
      $toast.warning(`Toujours non résolue (lettrage ${res.etatLettrage ?? '—'}, retry ${res.retryCount ?? '?'})`)
    } else {
      $toast.error('Facture introuvable')
    }
  } finally {
    const next = new Set(retrying.value)
    next.delete(row.invoiceUuid)
    retrying.value = next
    await Promise.all([store.fetchErrors(), store.fetchRuns({ limit: 50 })])
  }
}

// ── Actions manuelles (plage de dates) ──────────────────────────────────────
function todayIso(): string {
  return new Date().toISOString().substring(0, 10)
}
function firstOfMonthIso(): string {
  const d = new Date()
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().substring(0, 10)
}

const glFrom = ref(firstOfMonthIso())
const glTo = ref(todayIso())
const payFrom = ref(firstOfMonthIso())
const payTo = ref(todayIso())
const payRetryErrors = ref(false)

const glRun = ref<CegidJobProgress | null>(null)
const payRun = ref<CegidJobProgress | null>(null)
const launchingGl = ref(false)
const launchingPay = ref(false)

const wsStore = useWebSocketStore()
const { cegidJobProgress } = storeToRefs(wsStore)

const runningGl = computed(() => launchingGl.value || glRun.value?.status === 'running')
const runningPay = computed(() => launchingPay.value || payRun.value?.status === 'running')
const payPct = computed(() => {
  const r = payRun.value
  if (!r || !r.total) return null
  return Math.min(100, Math.round((r.processed / r.total) * 100))
})

function rangeInvalid(from: string, to: string): boolean {
  return !from || !to || from > to
}
function labelJob(job: string): string {
  return job === 'grand-livre' ? 'Sync grand livre' : 'Traitement paiements'
}
function statusBadge(status?: string): string {
  if (status === 'success') return 'badge-success'
  if (status === 'error') return 'badge-error'
  if (status === 'partial' || status === 'cancelled') return 'badge-warning'
  return 'badge-info'
}

// Réception de la progression poussée en WebSocket (event cegid-job-progress).
const toastedRuns = new Set<string>()
function handleTerminal(run: CegidJobProgress) {
  if (toastedRuns.has(run.runId)) return
  toastedRuns.add(run.runId)
  const c = run.counts ?? {}
  if (run.status === 'cancelled') {
    $toast.warning(`${labelJob(run.jobName)} annulé (${run.processed}/${run.total})`)
  } else if (run.status === 'error') {
    $toast.error(`${labelJob(run.jobName)} en échec : ${run.message ?? 'erreur'}`)
  } else if (run.jobName === 'grand-livre') {
    $toast.success(`Grand livre synchronisé — ${c.total ?? 0} écriture(s), ${c.ok ?? 0} upsert(s)`)
  } else {
    $toast.success(`Paiements traités — ${c.total ?? 0} réf., ${c.paymentRegistered ?? 0} paiement(s), ${c.ko ?? 0} KO`)
  }
  void Promise.all([store.fetchRuns({ limit: 50 }), store.fetchErrors()])
}

watch(cegidJobProgress, (msg) => {
  const run = (msg?.details ?? null) as CegidJobProgress | null
  if (!run?.runId) return
  if (run.jobName === 'grand-livre') glRun.value = run
  else if (run.jobName === 'invoice-statuses') payRun.value = run
  if (run.status !== 'running') handleTerminal(run)
})

async function launchGrandLivre() {
  if (rangeInvalid(glFrom.value, glTo.value)) {
    $toast.warning('Plage de dates invalide (début doit être ≤ fin)')
    return
  }
  launchingGl.value = true
  try {
    const res = await store.runGrandLivreSync(glFrom.value, glTo.value)
    if (res.ok) {
      const d = (res.data ?? {}) as { runId?: string }
      glRun.value = { runId: d.runId ?? '', jobName: 'grand-livre', status: 'running', processed: 0, total: 0, startedAt: new Date().toISOString() }
      $toast.info('Sync grand livre lancé')
    } else {
      $toast.warning(res.message ?? 'Impossible de lancer (déjà en cours ?)')
    }
  } finally {
    launchingGl.value = false
  }
}

async function launchPayments() {
  if (rangeInvalid(payFrom.value, payTo.value)) {
    $toast.warning('Plage de dates invalide (début doit être ≤ fin)')
    return
  }
  launchingPay.value = true
  try {
    const res = await store.runPaymentSync(payFrom.value, payTo.value, undefined, payRetryErrors.value)
    if (res.ok) {
      const d = (res.data ?? {}) as { runId?: string }
      payRun.value = { runId: d.runId ?? '', jobName: 'invoice-statuses', status: 'running', processed: 0, total: 0, startedAt: new Date().toISOString() }
      $toast.info('Traitement des paiements lancé')
    } else {
      $toast.warning(res.message ?? 'Impossible de lancer (déjà en cours ?)')
    }
  } finally {
    launchingPay.value = false
  }
}

async function cancelPayments() {
  if (!payRun.value?.runId) return
  const ok = await store.cancelJob(payRun.value.runId)
  if (ok) $toast.info('Annulation demandée…')
  else $toast.error('Annulation impossible (job déjà terminé ?)')
}

async function refreshAll() {
  await Promise.all([store.fetchRuns({ limit: 50 }), store.fetchErrors()])
}

onMounted(async () => {
  await refreshAll()
  // Reprise de l'affichage si un job tourne déjà (lancé depuis un autre onglet ou avant un reload).
  const [gl, pay] = await Promise.all([store.getActiveJob('grand-livre'), store.getActiveJob('invoice-statuses')])
  if (gl) glRun.value = gl
  if (pay) payRun.value = pay
})
</script>

<template>
  <div class="p-6">
    <Bs2Title title="Imports CEGID" subtitle="Suivi des synchronisations grand livre & statuts factures Odoo" />

    <div class="flex justify-end my-3">
      <ButtonDaisy text="Rafraîchir" variant="secondary" :icon="ArrowPathIcon" @click="refreshAll" />
    </div>

    <section class="card bg-base-100 shadow mt-4">
      <div class="card-body">
        <h2 class="card-title">Actions manuelles</h2>
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-2">
          <!-- Sync grand livre -->
          <div class="border border-base-300 rounded-lg p-4">
            <h3 class="font-semibold">Sync grand livre CEGID</h3>
            <p class="text-sm text-base-content/60 mt-1">
              Importe les écritures (date comptable dans la plage) depuis CEGID vers MongoDB.
            </p>
            <div class="flex flex-wrap items-end gap-3 mt-3">
              <label class="form-control">
                <span class="label-text">Du</span>
                <input v-model="glFrom" type="date" class="input input-bordered input-sm" />
              </label>
              <label class="form-control">
                <span class="label-text">Au</span>
                <input v-model="glTo" type="date" class="input input-bordered input-sm" />
              </label>
              <button class="btn btn-primary btn-sm" :disabled="runningGl" @click="launchGrandLivre">
                <span v-if="runningGl" class="loading loading-spinner loading-xs"></span>
                Lancer le sync
              </button>
            </div>
            <div v-if="glRun" class="mt-3 text-sm flex items-center gap-2">
              <span class="badge" :class="statusBadge(glRun.status)">{{ glRun.status }}</span>
              <span v-if="glRun.status === 'running'" class="loading loading-dots loading-xs"></span>
              <span v-else class="text-base-content/60">{{ glRun.counts?.total ?? 0 }} écriture(s)</span>
            </div>
          </div>

          <!-- Traitement paiements -->
          <div class="border border-base-300 rounded-lg p-4">
            <h3 class="font-semibold">Traitement paiements CEGID → Odoo</h3>
            <p class="text-sm text-base-content/60 mt-1">
              Lit le grand livre (déjà synchronisé) sur la plage et enregistre les paiements lettrés dans Odoo.
            </p>
            <div class="flex flex-wrap items-end gap-3 mt-3">
              <label class="form-control">
                <span class="label-text">Du</span>
                <input v-model="payFrom" type="date" class="input input-bordered input-sm" />
              </label>
              <label class="form-control">
                <span class="label-text">Au</span>
                <input v-model="payTo" type="date" class="input input-bordered input-sm" />
              </label>
              <label
                class="label cursor-pointer gap-2"
                title="Rejoue aussi les factures en erreur (supprime le paiement fantôme et ré-enregistre) et ignore le cooldown 24h."
              >
                <input v-model="payRetryErrors" type="checkbox" class="checkbox checkbox-sm" />
                <span class="label-text">Reprendre les erreurs</span>
              </label>
              <button class="btn btn-primary btn-sm" :disabled="runningPay" @click="launchPayments">
                <span v-if="runningPay" class="loading loading-spinner loading-xs"></span>
                Lancer le traitement
              </button>
              <button
                v-if="payRun?.status === 'running'"
                class="btn btn-outline btn-error btn-sm"
                @click="cancelPayments"
              >
                Annuler
              </button>
            </div>
            <div v-if="payRun" class="mt-3">
              <div class="flex items-center gap-2 text-sm">
                <span class="badge" :class="statusBadge(payRun.status)">{{ payRun.status }}</span>
                <span class="text-base-content/70">{{ payRun.processed }} / {{ payRun.total }}</span>
                <span v-if="payRun.counts?.paymentRegistered != null" class="text-base-content/50">
                  · {{ payRun.counts.paymentRegistered }} paiement(s)
                </span>
                <span v-if="payRun.counts?.ko" class="text-error">· {{ payRun.counts.ko }} KO</span>
              </div>
              <progress
                class="progress progress-primary w-full mt-1"
                :value="payPct ?? 0"
                max="100"
              ></progress>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="mt-4">
      <GridVue :list="store.errors" :cols="errorsColumns">
        <template #title>
          <h2 class="card-title">Factures en erreur ({{ store.errors.length }})</h2>
        </template>
      </GridVue>
    </section>

    <section class="mt-10">
      <GridVue :list="store.runs" :cols="runsColumns">
        <template #title>
          <h2 class="card-title">Derniers runs ({{ store.runs.length }})</h2>
        </template>
      </GridVue>
    </section>

    <!-- Modal : détail des erreurs d'un run -->
    <dialog class="modal" :class="{ 'modal-open': !!selectedRun }">
      <div class="modal-box max-w-3xl">
        <h3 class="font-bold text-lg">
          Détail du run — {{ selectedRun?.jobName }}
          <span v-if="selectedRun" class="badge ml-2" :class="statusBadge(selectedRun.status)">{{ selectedRun.status }}</span>
        </h3>

        <p v-if="selectedRun?.crashMessage" class="mt-3 text-error text-sm">
          <b>Crash :</b> {{ selectedRun.crashMessage }}
        </p>

        <div v-if="selectedRunErrorBreakdown.length" class="mt-4">
          <h4 class="font-semibold text-sm mb-1">Récap par code</h4>
          <div class="flex flex-wrap gap-2">
            <span v-for="b in selectedRunErrorBreakdown" :key="b.code" class="badge badge-outline">
              {{ b.code }} · {{ b.count }}
            </span>
          </div>
        </div>

        <div v-if="selectedRun?.errors?.length" class="mt-4 max-h-96 overflow-auto">
          <table class="table table-xs table-pin-rows">
            <thead>
              <tr><th>Référence</th><th>Code</th><th>Message</th></tr>
            </thead>
            <tbody>
              <tr v-for="(e, i) in selectedRun.errors" :key="i">
                <td class="font-mono">{{ e.ref }}</td>
                <td>{{ e.code }}</td>
                <td>{{ e.message }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <p v-else-if="!selectedRun?.crashMessage" class="mt-4 text-base-content/60">Aucune erreur détaillée.</p>

        <div class="modal-action">
          <button class="btn" @click="closeRunDetail">Fermer</button>
        </div>
      </div>
      <form method="dialog" class="modal-backdrop"><button @click="closeRunDetail">close</button></form>
    </dialog>
  </div>
</template>
